import Image from 'next/image';
import PinchosLogo from '../../public/assets/images/pinchos-circle-logo.png';
import { siteShell } from '../../lib/siteShell';
import WrapperWithBackground from '../_Theme/WrapperWithBackground';

interface ContentProps {
  locale?: string;
  /**
   * The site root's own page: a link to the country site's booking page
   * instead of the loading note. The root has no restaurant to book, and
   * it is where a guest or scanner lands on reservation.<domain>.
   */
  landing?: boolean;
}

/** Who we are, what the page is for, and our contact and policy pages. */
export function SiteShellContent({ locale, landing = false }: ContentProps) {
  const shell = siteShell(locale);
  return (
    <div className="flex min-h-[100dvh] w-full flex-1 flex-col items-center justify-between px-4 py-10 text-center text-white">
      <header className="flex flex-col items-center gap-4 pt-10">
        <Image src={PinchosLogo} alt={shell.brand} width={96} height={96} priority />
        <p className="text-sm uppercase tracking-huge text-darkGold">{shell.brand}</p>
        <h1 className="text-3xl">{shell.heading}</h1>
        <p className="max-w-md text-base opacity-80">{shell.description}</p>
        {landing ? (
          <a
            href={shell.booking.href}
            className="mt-4 inline-flex h-[46px] items-center rounded-full border border-darkGold bg-redThree px-6 font-medium text-white hover:text-darkGold focus:outline-none focus-visible:ring-2 focus-visible:ring-darkGold focus-visible:ring-offset-2"
          >
            {shell.booking.label}
          </a>
        ) : (
          <p className="text-sm opacity-60" aria-live="polite">
            {shell.loading}
          </p>
        )}
        <noscript>
          <p className="max-w-md text-base">{shell.noscript}</p>
        </noscript>
      </header>
      <footer className="flex flex-col items-center gap-3 pt-10 text-sm">
        <nav aria-label={shell.brand}>
          <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {shell.links.map((link) => (
              <li key={link.href}>
                <a href={link.href} className="underline underline-offset-4 hover:text-darkGold">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <p className="opacity-60">{shell.brand}</p>
      </footer>
    </div>
  );
}

/**
 * Shown until `_app` renders the page, and so the whole page a scanner that
 * doesn't run JavaScript sees.
 */
export default function SiteShell({ locale }: { locale?: string }) {
  return (
    <WrapperWithBackground>
      <SiteShellContent locale={locale} />
    </WrapperWithBackground>
  );
}
